import IMAGES from '@/assets/images';
import CustomImage from '@/components/CustomImage';
import {
  capitalize,
  errorToast,
  formatDate,
  normalizeColumnValue,
} from '@/utils';
import { Button, Flex, Image, Input, Modal, Tag } from 'antd';
import { useState } from 'react';
import {
  useApproveLeaveApplicationMutation,
  useRejectLeaveApplicationMutation,
} from '../../../../../../app/features/employee/emp-leave/emp-leave.api';
import type { LeaveApplication } from '../../../../../../app/features/employee/emp-leave/emp-leave.type';
import ConfirmationModal from '../../../../../../components/ConfirmationModal';
import CustomDrawer from '../../../../../../components/CustomDrawer';
import StatusTag from '../../../../../../components/StatusTag';
import useAuth from '../../../../../../hooks/useAuth';
import useModal from '../../../../../../hooks/useModal';
import { toast } from '../../../../../../lib/toast';
import AttatchedIcon from '../../_icons/AttatchedIcon';
import { LEAVE_STATUS_CONFIG } from './LeaveRequest';

const { TextArea } = Input;
const imageExtensions = ['jpg', 'jpeg', 'png', 'svg', 'webp'];

export default function LeaveDetails({
  open,
  handleClose,
  data,
}: {
  data: LeaveApplication;
  type?: string;
  open: boolean;
  handleClose: (value?: boolean) => void;
}) {
  const { userId } = useAuth();
  const [activeId, setActiveId] = useState<string | null>();
  const [rejectComment, setRejectComment] = useState('');
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [previewType, setPreviewType] = useState<'image' | 'pdf' | null>(null);

  const {
    open: confirmOpen,
    handleOpen,
    handleClose: confirmClose,
  } = useModal<any, 'CONFIRM'>();

  const [approveLeave, { isLoading: isApproving }] =
    useApproveLeaveApplicationMutation();
  const [rejectLeave, { isLoading: isRejecting }] =
    useRejectLeaveApplicationMutation();

  const {
    id,
    start_date,
    end_date,
    status,
    approver,
    employee,
    leave_type,
    created_at,
    reason,
    attachment_urls,
    comments,
  } = data || {};
  const startDate = start_date ? formatDate(start_date, 'MMM DD') : null;
  const endDate = end_date ? formatDate(end_date, 'MMM DD') : null;

  const handleApprove = async () => {
    const id = confirmOpen?.data?.id;
    if (!id) return;

    setActiveId(id);
    try {
      const response = await approveLeave({
        id,
        data: {
          approver_id: userId,
          comment: '',
        },
      }).unwrap();

      // Check if response has nested success/error structure
      if (response?.data?.success === false) {
        toast(
          'error',
          response.data.message || 'Failed to approve leave application',
        );
        return;
      }

      // If overall success is true, show success message
      if (response?.success === true) {
        toast('success', 'Leave application approved successfully');
        confirmClose();
        handleClose();
      }
    } catch (error) {
      errorToast(error, 'Failed to approve leave application');
    } finally {
      setActiveId(null);
    }
  };

  const handleReject = async () => {
    const id = confirmOpen?.data?.id;
    if (!id) return;

    setActiveId(id);

    try {
      const response = await rejectLeave({
        id,
        data: {
          approver_id: userId,
          comment: rejectComment ? rejectComment : '',
        },
      }).unwrap();

      if (response?.data?.success === false) {
        toast(
          'error',
          response.data.message || 'Failed to reject leave application',
        );
        return;
      }

      if (response?.success === true) {
        toast('success', 'Leave application rejected successfully');
        setRejectComment('');
        confirmClose();
        handleClose();
      }
    } catch (error) {
      errorToast(error, 'Failed to reject leave application');
    } finally {
      setActiveId(null);
    }
  };

  const handleConfirmSubmit = () => {
    if (confirmOpen.type === 'REJECT') {
      return handleReject();
    }

    if (confirmOpen.type === 'APPROVE') {
      return handleApprove();
    }
  };

  const handleConfirmClose = () => {
    setRejectComment(''); // ✅ clear comment
    confirmClose(); // existing close logic
  };

  const confirmationTextMap: Record<string, string> = {
    REJECT: 'You want to reject this leave application.',
    APPROVE: 'You want to approve this leave application.',
  };

  const getExtension = (url: string) =>
    url.split('.').pop()?.toLowerCase() || '';
  const isImage = (url: string) => imageExtensions.includes(getExtension(url));
  const isPdf = (url: string) => getExtension(url) === 'pdf';

  return (
    <>
      <CustomDrawer
        width={850}
        open={open}
        onClose={handleClose}
        title={'Leave Details'}
      >
        <div className="w-full max-w-4xl mx-auto p-3">
          {/* Header Section */}
          <div className="flex items-center justify-between border-b border-gray-700 pb-4">
            <div className="flex items-center gap-4">
              {/* <img
                src="https://i.pravatar.cc/100?img=1"
                alt="avatar"
                className="w-14 h-14 rounded-full object-cover"
              /> */}
              <CustomImage
                width={56}
                height={56}
                src={employee?.photo || IMAGES.avatar}
                className="mx-auto rounded-full object-cover"
              />
              <div>
                <p className="text-xs text-gray-400">
                  ID{' '}
                  {employee?.employee_id
                    ? `#${employee?.employee_id}`
                    : ': N/A'}
                </p>
                <div className="flex items-center gap-2">
                  <h2 className="text-lg font-semibold text-white">
                    {employee?.name}
                  </h2>
                  <StatusTag config={LEAVE_STATUS_CONFIG} value={status} />
                </div>
                <p className="text-sm text-gray-400">
                  {normalizeColumnValue(employee?.designation, '')}
                </p>
              </div>
            </div>

            <div className="flex flex-col items-end gap-2">
              <Tag
                color="default"
                className="border border-gray-500 text-gray-300"
              >
                {capitalize(leave_type)}
              </Tag>
              <p className="text-sm text-gray-400">
                Department :{' '}
                <span className="text-gray-300">
                  {normalizeColumnValue(employee?.department, 'N/A')}
                </span>
              </p>
            </div>
          </div>

          {/* Body Section */}
          <div className="grid grid-cols-2 gap-y-6 gap-x-10 mt-6">
            <div className="flex items-center justify-start gap-4">
              <p className="text-base text-gray-400">Date Range</p>
              <p className="text-white font-medium text-base">
                {startDate} - {endDate}
              </p>
            </div>

            <div className="flex items-center justify-start gap-4">
              <p className="text-base text-gray-400">Assigned To</p>
              <p className="text-white font-medium text-base">
                {approver?.name ?? 'N/A'}{' '}
              </p>
            </div>

            <div className="flex items-center justify-start gap-4">
              <p className="text-base text-gray-400">Applied On</p>
              <p className="text-white font-medium text-base">
                {formatDate(created_at) ?? '--'}
              </p>
            </div>

            <div className="flex items-start justify-start gap-4">
              <p className="text-base text-gray-400">Document</p>
              {/* <p className="text-white font-medium text-base relative top-[2.5px]">
                {attachment_urls?.length ? (
                  <Flex align="center" wrap gap={6}>
                    {attachment_urls?.map((el, idx) => (
                      <a
                        target="_blank"
                        className="text-sm !text-neutral-700 font-semibold hover:underline flex justify-start items-center gap-1 p-[5px] bg-zinc-300 rounded"
                        href={el}
                      >
                        <span>
                          <AttatchedIcon className="!text-[#007FC4] text-base" />
                        </span>{" "}
                        <span>
                          {" "}
                          Attachment {idx + 1}{" "}
                          {attachment_urls?.length - 1 > idx ? "," : ""}
                        </span>
                      </a>
                    ))}
                  </Flex>
                ) : (
                  "N/A"
                )}
              </p> */}
              <p className="text-white font-medium text-base relative top-[2.5px]">
                {attachment_urls?.length ? (
                  <Flex align="center" wrap gap={6}>
                    {attachment_urls.map((el, idx) => (
                      <a
                        key={idx}
                        onClick={() => {
                          if (isImage(el)) {
                            // Open images in modal
                            setPreviewType('image');
                            setPreviewUrl(el);
                          } else if (isPdf(el)) {
                            // PDF download
                            const link = document.createElement('a');
                            link.href = el;
                            link.download =
                              el.split('/').pop() || `attachment-${idx + 1}`; // fallback
                            link.click();
                          }
                        }}
                        className="text-sm !text-neutral-700 font-semibold hover:underline flex items-center gap-1 p-[5px] bg-zinc-300 rounded cursor-pointer"
                      >
                        <AttatchedIcon className="!text-[#007FC4] text-base" />
                        Attachment {idx + 1}
                      </a>
                    ))}
                  </Flex>
                ) : (
                  'N/A'
                )}
              </p>
            </div>

            <div className="col-span-2 flex items-center justify-start gap-4">
              <p className="text-base text-gray-400">Leave Reason</p>
              <p className="text-white font-medium text-base">
                {reason ?? 'N/A'}
              </p>
            </div>

            <div className="col-span-2 flex items-center justify-start gap-4">
              <p className="text-base text-gray-400">Rejection Reason</p>
              <p className="text-white font-medium text-base">
                {comments ? capitalize(comments) : 'N/A'}
              </p>
            </div>
          </div>

          {/* Buttons */}
          {!!(String(status).toLowerCase() === 'pending') && (
            <Flex align="center" justify="end" gap={16} className="mt-6">
              <Button
                size="middle"
                type="default"
                className="!bg-transparent !text-teal-500 hover:!text-teal-500 !border !border-teal-500 hover:!bg-transparent"
                onClick={() =>
                  handleOpen({
                    type: 'REJECT',
                    data: { id },
                  })
                }
                loading={isRejecting && activeId === id}
              >
                Reject
              </Button>
              <Button
                size="middle"
                type="primary"
                className="!text-[#444444] !text-base !font-semibold !bg-[#00C6AE] hover:!bg-[#00a896]"
                onClick={() =>
                  handleOpen({
                    type: 'APPROVE',
                    data: { id },
                  })
                }
                loading={isApproving && activeId === id}
              >
                Approve
              </Button>
            </Flex>
          )}
        </div>

        {/* Confirmation Modal */}
        <ConfirmationModal
          data={confirmOpen.data}
          type={confirmOpen.type}
          handleClose={handleConfirmClose}
          handleSubmit={handleConfirmSubmit}
          open={
            (confirmOpen.open && confirmOpen.type === 'REJECT') ||
            (confirmOpen.open && confirmOpen.type === 'APPROVE')
          }
          loading={isRejecting || isApproving}
          title="Are you sure?"
          subTitle={
            confirmOpen.type ? confirmationTextMap[confirmOpen.type] : ''
          }
          buttonName="Confirm"
          disabled={confirmOpen.type === 'REJECT' && !rejectComment.trim()}
        >
          {confirmOpen.type === 'REJECT' && (
            <div className="flex flex-col gap-2 mt-5">
              <div className="text-white text-base font-bold">Reason</div>
              <TextArea
                rows={4}
                size="middle"
                placeholder="Write here..."
                value={rejectComment}
                onChange={(e) => setRejectComment(e.target.value)}
                maxLength={100}
                className="!h-36 !rounded-xl"
              />
              <div className="text-gray-400 text-sm text-right">
                {rejectComment.length}/100
              </div>
            </div>
          )}
        </ConfirmationModal>
      </CustomDrawer>

      <Modal
        open={!!previewUrl}
        footer={null}
        width={previewType === 'pdf' ? 900 : 600}
        onCancel={() => setPreviewUrl(null)}
        title={previewType === 'pdf' ? 'PDF Preview' : 'Image Preview'}
        centered
        bodyStyle={{
          height: previewType === 'pdf' ? '80vh' : 'auto',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
        }}
      >
        {previewType === 'image' && previewUrl && (
          <Image src={previewUrl} width={500} preview={{ mask: 'View' }} />
        )}

        {previewType === 'pdf' && previewUrl && (
          <iframe
            src={previewUrl}
            width="100%"
            height="100%"
            style={{ border: 'none' }}
          />
        )}
      </Modal>
    </>
  );
}
